import type { Quality, CameraMode, HdrPref } from '../render/renderer';
import type { WinCondition } from '../shared/types';

export interface Prefs {
  name: string;
  /** Chosen bomber, 0-7. */
  slot: number;
  quality: Quality | 'auto';
  /**
   * What `auto` has settled on for this device, learned by watching frames.
   *
   * Kept apart from `quality` deliberately: writing a measured tier into
   * `quality` would take the player off `auto` and switch the watchdog off,
   * so the device would learn once and then stop learning.
   */
  autoTier: Quality | null;
  /** A tier that was tried and could not be held. Never tried again. */
  autoCeiling: Quality | null;
  /**
   * The build those two were learned on.
   *
   * A new build gets a clean slate, for the same reason the boot guard does:
   * the thing that could not hold ultra may have been the game, and a device
   * that has been written off never finds out it was fixed.
   */
  autoBuild: string;
  /**
   * Extended range on a display that reports it.
   *
   * `auto` asks `matchMedia('(dynamic-range: high)')`, which is a claim about
   * the panel and not about how the room it is in looks. The HDR path lifts
   * the grade's ceiling from 1 to 8, so a chain reaction that reads as bright
   * on a bench can read as painful on a couch at night -- and until this row
   * existed the only way back was `?gfx=nohdr`, a URL flag nobody who was not
   * told about it could find.
   */
  hdr: HdrPref;
  cameraMode: CameraMode;
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
  minimap: boolean;
  haptics: boolean;
  screenShake: number;
  /** Last used match setup, so Quick Match reuses your preferences. */
  arena: string;
  /**
   * Look to force on `arena`, or -1 to let the match seed pick. Tied to the
   * arena it was chosen on: switching arena resets it.
   */
  arenaLook: number;
  mode: 'ffa' | 'teams' | 'survival';
  bots: number;
  botLevel: number;
  lives: number;
  roundSeconds: number;
  /** How a match is won: elimination, first to N kills, or most kills on time. */
  winBy: WinCondition;
  killTarget: number;
  /**
   * Hits to kill. 1 is the one-blast game the series has always been.
   *
   * Three, now, with `resolve` and the series rule beside it. Measured over six
   * hundred eight-player matches on a three-minute round, the advantage that
   * used to decide the shipped game -- landing the early damage -- fell from
   * 3.6x chance to 1.5x, which is close to noise. See `shared/trial.ts` for
   * the URL flags that still override any of them.
   */
  hitPoints: number;
  /** Whoever is behind takes less damage and deals more, capped at 30% each way. */
  resolve: boolean;
  /** The series rule: specials lost on death, range and bomb count kept. */
  loseGearOnDeath: boolean;
  /**
   * The catch-up economy: drops weighted toward whoever is behind, plus the
   * Overload handed to a trailing player on respawn. Off is the flat table.
   */
  catchup: boolean;
  /** Mid-match supply drops. Off while it is being judged. */
  arenaEvents: boolean;
  /** A bomb already in the air can be punched again. */
  deflect: boolean;
  /**
   * Which generation of match rules the stored preferences were written by.
   *
   * The rules above are persisted, so a returning player's saved `winBy`
   * outlives a change to the defaults -- and the combination that produces is
   * not merely stale, it is one measured to break: hit points from the new
   * defaults with `kills` from the old ones reaches its target 6 times in 40
   * and runs out the clock the rest. A bump re-seeds the rule fields and
   * leaves everything else, so nobody loses their name or their bomber.
   */
  rulesVersion: number;
  suddenDeath: boolean;
  friendlyFire: boolean;
  itemDensity: number;
  /** Highest campaign stage cleared. */
  campaign: number;
  /**
   * Deepest room reached on an expedition, 1-based -- the room the run ended
   * in, not the number cleared. The reason to run again.
   */
  bestExpedition: number;
  /**
   * Unspent salvage, and what it has already bought.
   *
   * Local first and synced when signed in, in that order deliberately: a run
   * played signed-out still has to pay out, because the mode has to work
   * before anybody has an account. The server merges rather than overwrites,
   * so a stale client cannot spend somebody's progress twice or erase it.
   */
  salvage: number;
  /** Purchased unlock codes. See `UNLOCKS`. */
  unlocks: string[];
  /** Runs started and runs finished, for the outfitting screen to show. */
  expeditionRuns: number;
  expeditionClears: number;
  /** Local best scores, kept even when signed out. */
  bestSurvivalWave: number;
  totalWins: number;
  totalMatches: number;
}

/*
 * Deliberately still `bg9`, and not a leftover.
 *
 * A storage key is an address for data that already exists, not a name anybody
 * reads. Renaming it either throws away every player's name, bomber, settings,
 * unlocks and salvage, or buys a migration path that has to be carried
 * forever -- and the only thing either purchases is a tidier string in
 * devtools. The same reasoning keeps `RUN_KEY`, the boot guard's key, the D1
 * database, the R2 bucket and the Worker's own name on their original spellings while everything a
 * person reads became Detonarium.
 */
const KEY = 'bg9.prefs.v1';

/**
 * The generation of match rules `DEFAULT_PREFS` describes.
 *
 * Bump it whenever a change to the rule defaults would be incoherent when
 * half-applied over somebody's saved preferences, and add the fields it should
 * re-seed to `RULE_KEYS`.
 */
const RULES_VERSION = 1;

/** The preferences a rules bump re-seeds. Everything else is the player's. */
const RULE_KEYS = [
  'winBy',
  'killTarget',
  'lives',
  'hitPoints',
  'resolve',
  'loseGearOnDeath',
  'catchup',
  'arenaEvents',
  'deflect',
  'suddenDeath',
] as const;

export const DEFAULT_PREFS: Prefs = {
  name: '',
  slot: 0,
  quality: 'auto',
  autoTier: null,
  autoCeiling: null,
  autoBuild: '',
  hdr: 'auto',
  cameraMode: 'auto',
  master: 0.8,
  music: 0.5,
  sfx: 0.9,
  muted: false,
  minimap: true,
  haptics: true,
  screenShake: 1,
  arena: 'neon-grid',
  arenaLook: -1,
  mode: 'ffa',
  bots: 3,
  botLevel: 1,
  lives: 1,
  roundSeconds: 180,
  /*
   * Deathmatch on the clock. Elimination puts a beaten player on the bench for
   * the rest of the round, which is the wrong first impression for a game
   * somebody just pressed Quick Match on -- and a target of four kills cannot
   * survive hit points, which produce about one kill a match. The clock ends
   * it, and sudden death settles it if the scoreboard is level.
   */
  winBy: 'timed',
  killTarget: 4,
  hitPoints: 3,
  resolve: true,
  loseGearOnDeath: true,
  catchup: true,
  arenaEvents: false,
  deflect: true,
  rulesVersion: RULES_VERSION,
  suddenDeath: true,
  friendlyFire: false,
  itemDensity: 1,
  campaign: 0,
  bestExpedition: 0,
  salvage: 0,
  unlocks: [],
  expeditionRuns: 0,
  expeditionClears: 0,
  bestSurvivalWave: 0,
  totalWins: 0,
  totalMatches: 0,
};

const ADJECTIVES = ['Blast', 'Fuse', 'Nitro', 'Cinder', 'Volt', 'Rogue', 'Turbo', 'Quantum', 'Havoc', 'Neon'];
const NOUNS = ['Runner', 'Bomber', 'Spark', 'Pilot', 'Rebel', 'Ghost', 'Fox', 'Comet', 'Wolf', 'Ace'];

export function randomName(): string {
  const a = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const n = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  return `${a}${n}${Math.floor(Math.random() * 90 + 10)}`;
}

export function loadPrefs(): Prefs {
  let stored: Partial<Prefs> = {};
  try {
    stored = JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    stored = {};
  }
  const prefs = { ...DEFAULT_PREFS, ...stored };
  /*
   * Rules from before a bump are not merged, they are replaced.
   *
   * A partial merge is the dangerous case rather than the safe one: a saved
   * `winBy: 'kills'` sitting under a new default of three hit points is a
   * match that cannot reach its win condition, which reads to the player as
   * the game refusing to end.
   */
  if ((stored.rulesVersion ?? 0) < RULES_VERSION) {
    for (const k of RULE_KEYS) (prefs as Record<string, unknown>)[k] = DEFAULT_PREFS[k];
    prefs.rulesVersion = RULES_VERSION;
  }
  if (!prefs.name) prefs.name = randomName();
  return prefs;
}

export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    /* private mode: preferences are simply not persisted */
  }
}

/**
 * Picks a starting quality tier from what we can cheaply detect.
 *
 * Deliberately not from the user agent. "Request desktop site" rewrites it, and
 * a Pixel 10 Pro XL asking for the desktop site reads as `X11; Linux x86_64` --
 * no `Android`, no `Mobile` -- so a phone fell through to the desktop branch
 * and was handed the *ultra* tier, 4096px shadow maps and all. `screen` does
 * not lie in that mode: the same device reported 524 x 1166 either way.
 *
 * `deviceMemory` cannot help separate them either. The spec rounds it to a
 * power of two and caps it at 8, so `mem >= 8` is true of every phone with 8GB
 * as well as every workstation, and a 16GB device reports the same 8.
 *
 * Players can always override it, and this is only a starting point: the loop
 * in `ui/autoquality.ts` corrects it from measured frames, in both directions.
 * A tier is not the last word on what a device can do -- it is the first
 * guess, and the frames that follow are the evidence.
 */
export function detectQuality(): Quality {
  const cores = navigator.hardwareConcurrency || 4;
  const mem = (navigator as unknown as { deviceMemory?: number }).deviceMemory;
  const dpr = window.devicePixelRatio || 1;
  const pixels = window.innerWidth * window.innerHeight * dpr * dpr;

  const touch = (navigator.maxTouchPoints ?? 0) > 0;
  // The short edge of the *screen*, not the window: 820 CSS pixels puts phones
  // and small tablets on one side and every laptop on the other.
  const shortEdge = Math.min(window.screen.width, window.screen.height);
  const handheld = touch && shortEdge <= 820;

  /*
   * `low` needs evidence, and on WebKit there is none to be had.
   *
   * `deviceMemory` is a Chromium API: Safari does not implement it, so the old
   * `mem ?? 4` filled the gap with a number and then `cores <= 4 || mem <= 3`
   * read the remaining half of the test -- a core count WebKit reports
   * conservatively -- as though it were two agreeing signals. An iPhone 16 Pro
   * Max came out `low`, which sets `detail: 0`, which draws no scenery ring at
   * all, on a device that renders the whole ladder without complaint.
   *
   * So a browser that reports no memory gets no guess: it falls through to the
   * pixel count, and the frame-rate watchdog drops a tier if the device cannot
   * actually hold it. Measuring beats guessing, and the watchdog is already
   * the measurement.
   */
  if (handheld && mem !== undefined && (cores <= 4 || mem <= 3)) return 'low';
  if (handheld) return pixels > 3.2e6 ? 'medium' : 'high';
  // A big touchscreen -- a tablet, or a laptop with a touch panel. Capable, but
  // ultra's 4096px shadow map is not a thing to hand something battery-powered
  // on a guess.
  if (touch) return 'high';
  // `mem ?? 4` here only to keep the desktop branch exactly as it was: a
  // browser that reports nothing has never reached ultra and still does not.
  if (cores >= 8 && (mem ?? 4) >= 8) return 'ultra';
  if (cores >= 4) return 'high';
  return 'medium';
}
